// Contrail V3.7.0 — object sheet that slides up from the bottom (peek / expanded).
// Actions: Follow, Share (expiring link through the phone's share menu), Navigate (Google Maps directions).
// History is added in V3.8.0.
import { useEffect, useState } from "react";
import type { Device } from "../lib/supabase";
import { ago, fijiTime, kmh, liveSpeed, statusOf, STATUS_LABEL } from "../lib/format";
import { iconSvg } from "../lib/icons";
import { dms, fmtDistance, metres, zonesAt, zoneStack } from "../lib/geo";
import type { Zone } from "../lib/org";
import { supabase } from "../lib/supabase";
import { QUALITY_NOTE, type Quality } from "../../netlify/lib/gpsquality";
import type { MyPos } from "./MobileMap";
import { stoppedLabel, useStoppedFor } from "../lib/stopped";

const DURATIONS: [number, string][] = [[1, "1 hour"], [4, "4 hours"], [24, "24 hours"]];

async function createShare(device: string, hours: number) {
  const { data } = await supabase.auth.getSession();
  const r = await fetch("/api/share", { method: "POST", headers: { Authorization: `Bearer ${data.session?.access_token ?? ""}`, "Content-Type": "application/json" },
    body: JSON.stringify({ device, hours }) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error ?? `Couldn't create the link (${r.status})`);
  return `${window.location.origin}/s/${j.token}`;
}

export default function ObjectSheet({ device, zones, now, me, following, onFollow, onClose, onToast }: {
  device: Device; zones: Zone[]; now: number; me: MyPos | null; following: boolean;
  onFollow: (on: boolean) => void; onClose: () => void; onToast: (m: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setOpen(false); setSharing(false); }, [device.id]);
  const s = device.state;
  const st = statusOf(s, now);
  const stoppedFor = stoppedLabel(useStoppedFor(device.id, s, now), now);   // V3.9.2
  const zs = zonesAt(zones, s?.lat, s?.lng);
  const a = (s?.attrs ?? {}) as Record<string, unknown>;
  const has = s?.lat != null && s?.lng != null;
  const dist = me && has ? metres([me.lng, me.lat], [s!.lng!, s!.lat!]) : null;

  async function share(hours: number) {
    setBusy(true);
    try {
      const url = await createShare(device.id, hours);
      const text = `Live location of ${device.name} (link expires in ${hours === 1 ? "1 hour" : `${hours} hours`})`;
      if (navigator.share) { try { await navigator.share({ title: device.name, text, url }); } catch { /* cancelled */ } }
      else { await navigator.clipboard.writeText(url).catch(() => {}); onToast("Link copied — paste it into a message."); }
      setSharing(false);
    } catch (e: any) { onToast(e.message); } finally { setBusy(false); }
  }
  async function copyCoords() {
    if (!has) return;
    try { await navigator.clipboard.writeText(`${s!.lat!.toFixed(6)}, ${s!.lng!.toFixed(6)}`); onToast("Coordinates copied"); } catch { onToast("Couldn't copy on this phone"); }
  }

  return (
    <section className={`m-sheet ${open ? "open" : ""}`} aria-label={`${device.name} details`}>
      <button type="button" className="m-grab" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? "Show less" : "Show more"}><i /></button>
      <div className="m-sheet-head">
        <span className={`licon ${st}`} dangerouslySetInnerHTML={{ __html: iconSvg(device.icon, 16) }} />
        <div className="m-sheet-title">
          <b>{device.name}</b>
          <span className="sub"><span className={`pill ${st}`}>{STATUS_LABEL[st]}{stoppedFor && <span className="pill-time"> · {stoppedFor}</span>}</span> {liveSpeed(s, st)} · {ago(s?.seen_at, now)}</span>
          {zs.length > 0 && <span className="sub m-zone" title={zs.map((z) => z.name).join(" › ")}>In {zoneStack(zs)}</span>}
        </div>
        <button type="button" className="m-x" onClick={onClose} aria-label="Close">×</button>
      </div>
      {s?.quality && s.quality !== "good" && <div className="m-warn">⚠ {QUALITY_NOTE[s.quality as Quality]}</div>}
      <div className="m-actions">
        <button type="button" className={following ? "on" : ""} aria-pressed={following} onClick={() => onFollow(!following)} disabled={!has}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="3" /><circle cx="12" cy="12" r="8" /></svg>{following ? "Following" : "Follow"}</button>
        <button type="button" onClick={() => setSharing(!sharing)} aria-expanded={sharing}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" /></svg>Share</button>
        <a className={has ? "" : "disabled"} href={has ? `https://www.google.com/maps/dir/?api=1&destination=${s!.lat},${s!.lng}` : undefined} target="_blank" rel="noopener noreferrer">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m3 11 19-9-9 19-2-8z" /></svg>Navigate</a>
      </div>
      {sharing && (
        <div className="m-share">
          <p className="sub">Anyone with the link sees <b>{device.name}</b>'s live position until it expires — no login, no history.</p>
          <div className="chips">{DURATIONS.map(([h, t]) => <button key={h} type="button" disabled={busy} onClick={() => share(h)}>{t}</button>)}</div>
        </div>
      )}
      {open && (
        <dl className="m-facts">
          <div><dt>Last report</dt><dd>{fijiTime(s?.seen_at)}</dd></div>
          <div><dt>Position</dt><dd>{has ? <button type="button" className="linklike mono" onClick={copyCoords}>{s!.lat!.toFixed(5)}, {s!.lng!.toFixed(5)}</button> : "—"}
            {has && <span className="sub mono"> {dms(s!.lat!, s!.lng!)}</span>}</dd></div>
          {dist != null && <div><dt>From me</dt><dd>{fmtDistance(dist)}</dd></div>}
          <div><dt>Ignition</dt><dd>{s?.ignition == null ? "—" : s.ignition ? "On" : "Off"}</dd></div>
          <div><dt>Battery</dt><dd className="mono">{s?.battery != null ? `${s.battery}%` : "—"}</dd></div>
          <div><dt>Odometer</dt><dd className="mono">{a.totalDistance != null ? `${(Number(a.totalDistance) / 1000).toFixed(1)} km` : "—"}</dd></div>
          <div><dt>GPS</dt><dd>{s?.quality === "no_fix" ? "No fix" : s?.quality === "low" ? "Weak" : s?.quality === "suspect" ? "Being checked" : s ? "Good" : "—"}</dd></div>
        </dl>
      )}
    </section>
  );
}
